import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { format } from "date-fns";
import { Ban, CalendarPlus, FileUp, ImagePlus, Pencil, ShieldPlus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "../hooks/usePageMeta";
import WhatsAppComposer from "../components/WhatsAppComposer";
import ApiKeyManager from "../components/ApiKeyManager";
import { api, apiError } from "../lib/api";
import { EVENT_TYPES, GAMES, posterFor } from "../lib/calendar";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const blank = {
  title: "",
  game: "Pokémon TCG",
  event_type: "challenge",
  description: "",
  start: "",
  end: "",
  location: "Hex Labs Games, Maldon",
  ticket_url: "",
  price: "",
  image_url: "",
  sold_out: false,
};

const toInput = (iso) => (iso ? iso.slice(0, 16) : "");

export default function AdminPage() {
  const { user, loading } = useAuth();
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [events, setEvents] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "" });
  const [wipeCategory, setWipeCategory] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [extraDays, setExtraDays] = useState([]);
  const posterRef = useRef(null);
  usePageMeta("Admin Dashboard");
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const refresh = useCallback(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
    api.get("/tournaments").then(({ data }) => setTournaments(data)).catch(() => {});
    api.get("/admins").then(({ data }) => setAdmins(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  if (loading) return <p className="mx-auto max-w-5xl px-5 py-16 text-neutral-500">Loading…</p>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitEvent = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, start: `${form.start}:00`, end: null };
    const days = extraDays.filter(Boolean);
    try {
      if (editingId) {
        await api.put(`/events/${editingId}`, payload);
        toast.success("Event updated");
      } else if (days.length > 0) {
        const starts = [form.start, ...days].map((d) => `${d}:00`);
        const { data } = await api.post("/events/bulk", { ...payload, starts });
        toast.success(`${data.length} days created (Day 1–${data.length})`);
      } else {
        await api.post("/events", payload);
        toast.success("Event created");
      }
      setForm(blank);
      setExtraDays([]);
      setEditingId(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  const editEvent = (ev) => {
    setEditingId(ev.id);
    setExtraDays([]);
    setForm({
      title: ev.title, game: ev.game, event_type: ev.event_type, description: ev.description || "",
      start: toInput(ev.start), end: toInput(ev.end), location: ev.location || "",
      ticket_url: ev.ticket_url || "", price: ev.price || "", image_url: ev.image_url || "",
      sold_out: !!ev.sold_out,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeEvent = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event deleted");
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    }
  };

  const uploadFiles = async (files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/tournaments/upload", fd);
        toast.success(`${data.name} imported as ${data.category}${data.ranked ? "" : " (unranked)"}`);
      } catch (err) {
        toast.error(`${file.name}: ${apiError(err.response?.data?.detail, err.message)}`);
      }
    }
    refresh();
  };

  const removeTournament = async (id) => {
    try {
      await api.delete(`/tournaments/${id}`);
      toast.success("Tournament removed");
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    }
  };

  const toggleSoldOut = async (ev) => {
    try {
      await api.patch(`/events/${ev.id}/sold-out?sold_out=${!ev.sold_out}`);
      toast.success(ev.sold_out ? "Marked as on sale" : "Marked as sold out");
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    }
  };

  const uploadPoster = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/poster", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Poster uploaded");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    } finally {
      setUploading(false);
      if (posterRef.current) posterRef.current.value = "";
    }
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admins", newAdmin);
      toast.success(`${newAdmin.email} can now sign in as an admin`);
      setNewAdmin({ name: "", email: "", password: "" });
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    } finally {
      setBusy(false);
    }
  };

  const removeAdmin = async (id) => {
    try {
      await api.delete(`/admins/${id}`);
      toast.success("Admin removed");
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    }
  };

  const wipeResults = async () => {
    try {
      const { data } = await api.delete(`/tournaments?category=${wipeCategory}`);
      toast.success(`${data.deleted} tournament${data.deleted === 1 ? "" : "s"} wiped. Leaderboards reset.`);
      refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, err.message));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 lg:px-10 py-10 lg:py-16" data-testid="admin-page">
      <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
        ADMIN <span className="text-primary">DASHBOARD</span>
      </h1>
      <p className="mt-3 text-sm text-neutral-400">Signed in as {user.email}</p>

      <Tabs defaultValue="events" className="mt-8">
        <TabsList className="bg-white/5 border border-white/10 rounded-full">
          <TabsTrigger value="events" data-testid="admin-tab-events" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black text-xs uppercase tracking-wider px-4">
            Events
          </TabsTrigger>
          <TabsTrigger value="tdf" data-testid="admin-tab-tdf" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black text-xs uppercase tracking-wider px-4">
            TDF Uploads
          </TabsTrigger>
          <TabsTrigger value="manage" data-testid="admin-tab-manage" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black text-xs uppercase tracking-wider px-4">
            Admins & Data
          </TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="admin-tab-whatsapp" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black text-xs uppercase tracking-wider px-4">
            WhatsApp
          </TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="mt-8">
          <form onSubmit={submitEvent} className="grid gap-4 sm:grid-cols-2 border border-white/10 rounded-md p-5 bg-white/[0.02]" data-testid="event-form">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Title</Label>
              <Input required data-testid="event-title-input" value={form.title} onChange={set("title")} className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Game</Label>
              <select data-testid="event-game-select" value={form.game} onChange={set("game")} className="mt-1.5 w-full h-10 px-3 rounded-sm bg-[#121212] border border-white/10 text-sm">
                {GAMES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Type</Label>
              <select data-testid="event-type-select" value={form.event_type} onChange={set("event_type")} className="mt-1.5 w-full h-10 px-3 rounded-sm bg-[#121212] border border-white/10 text-sm">
                {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Date & start time</Label>
              <Input required type="datetime-local" data-testid="event-start-input" value={form.start} onChange={set("start")} className="mt-1.5 bg-white/5 border-white/10" />
              {!editingId && extraDays.length > 0 && (
                <p className="text-[11px] text-primary font-mono mt-1">Day 1</p>
              )}
            </div>
            {!editingId && (
              <div className="sm:col-span-2 border border-white/10 rounded-md p-4 bg-white/[0.02]" data-testid="multi-day-section">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-neutral-400">Extra days (prerelease weekends etc.)</Label>
                    <p className="text-[11px] text-neutral-500 mt-1">
                      Each date becomes its own event titled “{form.title || "Event title"} — Day 1, Day 2 …”.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="add-day-btn"
                    onClick={() => setExtraDays((d) => [...d, ""])}
                    className="rounded-full border-white/20 hover:border-primary hover:text-primary"
                  >
                    <CalendarPlus className="h-4 w-4 mr-2" /> Add another day
                  </Button>
                </div>
                {extraDays.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {extraDays.map((value, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="font-mono text-[11px] text-primary w-12 shrink-0">Day {i + 2}</span>
                        <Input
                          required
                          type="datetime-local"
                          data-testid={`extra-day-input-${i}`}
                          value={value}
                          onChange={(e) => setExtraDays((d) => d.map((v, j) => (j === i ? e.target.value : v)))}
                          className="bg-white/5 border-white/10"
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          data-testid={`remove-day-btn-${i}`}
                          onClick={() => setExtraDays((d) => d.filter((_, j) => j !== i))}
                          className="text-neutral-400 hover:text-destructive shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Shopify ticket URL</Label>
              <Input data-testid="event-ticket-input" value={form.ticket_url} onChange={set("ticket_url")} placeholder="https://hexlabsgames.com/products/..." className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Entry cost</Label>
              <Input data-testid="event-price-input" value={form.price} onChange={set("price")} placeholder="12" className="mt-1.5 bg-white/5 border-white/10" />
              <p className="text-[11px] text-neutral-500 mt-1">Just type the number — it's saved as £ with pence (12 → £12.00).</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Location</Label>
              <Input data-testid="event-location-input" value={form.location} onChange={set("location")} className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Description</Label>
              <Textarea data-testid="event-description-input" value={form.description} onChange={set("description")} className="mt-1.5 bg-white/5 border-white/10" rows={3} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Poster / set artwork</Label>
              <div className="mt-1.5 flex flex-col sm:flex-row gap-3 sm:items-center">
                <img
                  src={posterFor({ image_url: form.image_url, game: form.game, event_type: form.event_type })}
                  alt="Poster preview"
                  data-testid="poster-preview"
                  className="h-20 w-32 object-cover rounded-sm border border-white/10"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="poster-upload-btn"
                    disabled={uploading}
                    onClick={() => posterRef.current?.click()}
                    className="rounded-full border-white/20 hover:border-primary hover:text-primary"
                  >
                    <ImagePlus className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Upload image"}
                  </Button>
                  {form.image_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      data-testid="poster-clear-btn"
                      onClick={() => setForm((f) => ({ ...f, image_url: "" }))}
                      className="rounded-full text-neutral-400 hover:text-destructive"
                    >
                      Use default artwork
                    </Button>
                  )}
                  <input
                    ref={posterRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="poster-file-input"
                    onChange={(e) => e.target.files[0] && uploadPoster(e.target.files[0])}
                  />
                </div>
              </div>
              <p className="text-[11px] text-neutral-500 mt-2">
                JPG, PNG, WEBP or GIF up to 8MB. Leave empty to use the default artwork for the selected game.
              </p>
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <Button type="submit" disabled={busy} data-testid="event-submit-btn" className="bg-primary text-black font-bold uppercase tracking-wider rounded-full hover:bg-[#FFA94D]">
                <CalendarPlus className="h-4 w-4 mr-2" />
                {editingId ? "Save changes" : extraDays.length > 0 ? `Create ${extraDays.length + 1} days` : "Create event"}
              </Button>
              {editingId && (
                <Button type="button" variant="outline" data-testid="event-cancel-btn" className="rounded-full border-white/20" onClick={() => { setEditingId(null); setForm(blank); setExtraDays([]); }}>
                  Cancel
                </Button>
              )}
            </div>
          </form>

          <h2 className="mt-10 text-base md:text-lg font-display">All events ({events.length})</h2>
          <div className="mt-3 space-y-2" data-testid="admin-event-list">
            {events.map((ev) => (
              <div key={ev.id} className="flex items-center gap-3 p-3 border border-white/10 rounded-md" data-testid={`admin-event-${ev.id}`}>
                <div className="font-mono text-xs text-neutral-500 min-w-[110px]">{format(new Date(ev.start), "d MMM yy HH:mm")}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">
                    {ev.title}
                    {ev.sold_out && (
                      <span className="ml-2 text-[10px] font-mono uppercase text-loss">Sold out</span>
                    )}
                  </div>
                  <div className="text-[11px] text-neutral-500">{ev.event_type} · {ev.game}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`sold-out-toggle-${ev.id}`}
                  onClick={() => toggleSoldOut(ev)}
                  className={`rounded-full text-[11px] uppercase tracking-wider shrink-0 ${
                    ev.sold_out
                      ? "border-destructive/60 text-destructive hover:text-white"
                      : "border-white/20 text-neutral-400 hover:border-destructive hover:text-destructive"
                  }`}
                >
                  <Ban className="h-3.5 w-3.5 mr-1.5" /> {ev.sold_out ? "On sale" : "Sold out"}
                </Button>
                <Button size="icon" variant="ghost" data-testid={`edit-event-${ev.id}`} onClick={() => editEvent(ev)} className="text-neutral-400 hover:text-primary">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" data-testid={`delete-event-${ev.id}`} onClick={() => removeEvent(ev.id)} className="text-neutral-400 hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {events.length === 0 && <p className="text-sm text-neutral-500">No events yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="tdf" className="mt-8">
          <div
            data-testid="tdf-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(Array.from(e.dataTransfer.files)); }}
            onClick={() => fileRef.current?.click()}
            className={`border-2 border-dashed rounded-md p-10 text-center cursor-pointer transition-colors duration-200 ${
              dragging ? "border-primary bg-primary/5" : "border-white/20 hover:border-primary"
            }`}
          >
            <FileUp className="h-8 w-8 mx-auto text-primary mb-3" />
            <p className="text-sm">Drop .TDF files here, or click to browse</p>
            <p className="text-[11px] text-neutral-500 mt-1">
              Friendly, Challenge and Cup files earn ranking points. Prerelease files are stored unranked.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".tdf,.xml"
              multiple
              data-testid="tdf-file-input"
              className="hidden"
              onChange={(e) => uploadFiles(Array.from(e.target.files))}
            />
          </div>
          <Button
            className="mt-3 rounded-full bg-primary text-black font-bold uppercase tracking-wider hover:bg-[#FFA94D]"
            data-testid="tdf-browse-btn"
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-2" /> Select files
          </Button>

          <h2 className="mt-10 text-base md:text-lg font-display">Imported tournaments ({tournaments.length})</h2>
          <div className="mt-3 space-y-2" data-testid="admin-tournament-list">
            {tournaments.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 border border-white/10 rounded-md" data-testid={`admin-tournament-${t.id}`}>
                <div className="font-mono text-xs text-neutral-500 min-w-[90px]">{format(new Date(t.date), "d MMM yyyy")}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.name}</div>
                  <div className="text-[11px] text-neutral-500 uppercase">
                    {t.category}{t.ranked ? "" : " · unranked"} · {t.player_count} players
                  </div>
                </div>
                <Button size="icon" variant="ghost" data-testid={`delete-tournament-${t.id}`} onClick={() => removeTournament(t.id)} className="text-neutral-400 hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {tournaments.length === 0 && <p className="text-sm text-neutral-500">No tournaments imported yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="manage" className="mt-8">
          <h2 className="text-base md:text-lg font-display">Create admin account</h2>
          <form onSubmit={createAdmin} className="mt-3 grid gap-4 sm:grid-cols-3 border border-white/10 rounded-md p-5 bg-white/[0.02]" data-testid="admin-create-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Name</Label>
              <Input required data-testid="new-admin-name-input" value={newAdmin.name} onChange={(e) => setNewAdmin((a) => ({ ...a, name: e.target.value }))} className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Email</Label>
              <Input required type="email" data-testid="new-admin-email-input" value={newAdmin.email} onChange={(e) => setNewAdmin((a) => ({ ...a, email: e.target.value }))} className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-neutral-400">Password (min 6)</Label>
              <Input required type="password" minLength={6} data-testid="new-admin-password-input" value={newAdmin.password} onChange={(e) => setNewAdmin((a) => ({ ...a, password: e.target.value }))} className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div className="sm:col-span-3">
              <Button type="submit" disabled={busy} data-testid="create-admin-btn" className="bg-primary text-black font-bold uppercase tracking-wider rounded-full hover:bg-[#FFA94D]">
                <ShieldPlus className="h-4 w-4 mr-2" /> Create admin
              </Button>
            </div>
          </form>

          <h2 className="mt-10 text-base md:text-lg font-display">Admin accounts ({admins.length})</h2>
          <div className="mt-3 space-y-2" data-testid="admin-account-list">
            {admins.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 border border-white/10 rounded-md" data-testid={`admin-account-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{a.name}</div>
                  <div className="text-[11px] text-neutral-500">{a.email}{a.id === user.id ? " · you" : ""}</div>
                </div>
                {a.id !== user.id && (
                  <Button size="icon" variant="ghost" data-testid={`delete-admin-${a.id}`} onClick={() => removeAdmin(a.id)} className="text-neutral-400 hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          <h2 className="mt-12 text-base md:text-lg font-display">API access</h2>
          <p className="mt-2 text-sm text-neutral-400 max-w-2xl">
            Generate keys for other services to pull your event list (name, date/time and ticket price only).
          </p>
          <div className="mt-4 border border-white/10 rounded-md p-5 bg-white/[0.02]">
            <ApiKeyManager />
          </div>

          <h2 className="mt-12 text-base md:text-lg font-display text-destructive">Danger zone — wipe results</h2>
          <div className="mt-3 border border-destructive/40 rounded-md p-5 bg-destructive/[0.04]">
            <p className="text-sm text-neutral-300">
              Deletes imported tournament results and resets the leaderboards. Upcoming events are never touched —
              delete those individually from the Events tab.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <select
                data-testid="wipe-category-select"
                value={wipeCategory}
                onChange={(e) => setWipeCategory(e.target.value)}
                className="h-10 px-3 rounded-sm bg-[#121212] border border-white/10 text-sm"
              >
                <option value="all">All results (full reset)</option>
                <option value="friendly">Friendly only</option>
                <option value="challenge">Challenge only</option>
                <option value="cup">Cup only</option>
                <option value="prerelease">Prerelease only</option>
              </select>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" data-testid="wipe-results-btn" className="rounded-full font-bold uppercase tracking-wider">
                    <Trash2 className="h-4 w-4 mr-2" /> Wipe results
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="glass border-white/10" data-testid="wipe-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display tracking-tight">Wipe tournament results?</AlertDialogTitle>
                    <AlertDialogDescription className="text-neutral-400">
                      This permanently deletes {wipeCategory === "all" ? "all imported tournaments" : `all ${wipeCategory} tournaments`} and
                      the leaderboard points from them. Upcoming events stay in place. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="wipe-cancel-btn" className="rounded-full border-white/20">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="wipe-confirm-btn" onClick={wipeResults} className="rounded-full bg-destructive text-white font-bold uppercase tracking-wider">
                      Yes, wipe it
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="whatsapp" className="mt-8">
          <h2 className="text-base md:text-lg font-display">WhatsApp message builder</h2>
          <p className="mt-2 text-sm text-neutral-400 max-w-2xl">
            Pick a month, choose a style, then copy and paste straight into your WhatsApp group or broadcast.
          </p>
          <div className="mt-6 border border-white/10 rounded-md p-5 bg-white/[0.02]">
            <WhatsAppComposer events={events} tournaments={tournaments} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
